import React, { useState, useEffect } from 'react';
import {
  UserButton, SignOutButton, useUser, useOrganization, useOrganizationList,
} from '@clerk/clerk-react';
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import { trialDaysLeft, trialTierColors } from './TrialBanner';

function CustomOrgSwitcher() {
  // custom org switcher until clerk supports search
  const { organization } = useOrganization();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { userMemberships, setActive } = useOrganizationList({
    userMemberships: { infinite: true, pageSize: 100 },
  });

  useEffect(() => {
    if (userMemberships.hasNextPage && !userMemberships.isFetching) {
      userMemberships.fetchNext();
    }
  }, [userMemberships.hasNextPage, userMemberships.isFetching]);

  const filtered = (userMemberships.data ?? []).filter(
    (mem) => mem.organization.name.toLowerCase().includes(query.toLowerCase()),
  );

  const handleSelect = (orgId) => {
    setActive({ organization: orgId }).then(() => window.location.reload());
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-surface-700 bg-white border border-surface-200 rounded-xl hover:border-surface-300 transition-colors"
      >
        <span className="truncate">{organization?.name ?? '—'}</span>
        <svg className="w-3.5 h-3.5 ml-2 flex-shrink-0 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setQuery(''); }} aria-hidden="true" />
          <div className="absolute bottom-full mb-1 left-0 right-0 bg-white rounded-xl border border-surface-200 shadow-xl z-50 overflow-hidden">
            <div className="p-2 border-b border-surface-100">
              <input
                autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search organizations..."
                className="w-full px-2.5 py-1.5 text-sm bg-surface-50 border border-surface-200 rounded-lg outline-none focus:border-surface-400"
              />
            </div>
            <div className="max-h-52 overflow-y-auto py-1">
              {filtered.map((mem) => (
                <button
                  key={mem.organization.id}
                  type="button"
                  onClick={() => handleSelect(mem.organization.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-50 transition-colors truncate ${mem.organization.id === organization?.id ? 'font-semibold text-surface-900' : 'text-surface-700'}`}
                >
                  {mem.organization.name}
                </button>
              ))}
              {userMemberships.isLoading && (
                <p className="px-3 py-2 text-xs text-surface-400">Loading...</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Navbar(props) {
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(location.pathname.split('/')[1]);
  // keep the highlight in sync with the url so palette navigation and
  // back/forward update the selected item, not just menu clicks
  useEffect(() => {
    setActiveMenu(location.pathname.split('/')[1]);
  }, [location]);
  const { orgInfo } = props;
  const { user } = useUser();
  const showSwitcher = user.organizationMemberships.length > 1;

  const trialDays = trialDaysLeft(orgInfo);
  let trialChipText = null;
  if (trialDays !== null) {
    trialChipText = trialDays <= 0
      ? 'Free trial ended'
      : `Free trial · ${trialDays} ${trialDays === 1 ? 'day' : 'days'} left`;
  }

  const menuItemStyles = {
    button: {
      padding: '10px 12px',
      margin: '2px 8px',
      borderRadius: '10px',
      fontSize: '0.8125rem',
      fontWeight: 500,
      color: '#78716C',
      letterSpacing: '-0.01em',
      transition: 'all 0.15s ease',
      '&:hover': {
        backgroundColor: '#F5F5F4',
        color: '#292524',
      },
    },
  };

  const isActive = (menuKey) => {
    if (menuKey === '' && activeMenu === '') return true;
    return activeMenu === menuKey;
  };

  return (
    <Sidebar
      className="h-screen border-none"
      width="240px"
      backgroundColor="#FAFAF9"
      style={{ border: 'none' }}
    >
      <div className="flex flex-col min-h-full">
        {/* Logo section */}
        <div className="px-4 pt-5 pb-2 flex-shrink-0">
          <Link
            to="/"
            onClick={() => setActiveMenu('')}
            className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-surface-100 transition-colors group"
          >
            <img src="/ts_logo_2024.png" alt="Trackstar Logo" className="h-9 w-9 rounded-lg" />
            <span className="text-base font-semibold text-surface-900 tracking-tight">Trackstar</span>
          </Link>
        </div>

        {/* Search affordance — opens the command palette (also bound to cmd/ctrl+k) */}
        <div className="px-4 pt-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('pokecenter:open-search'))}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-surface-200 bg-surface-100/50 text-surface-500 hover:bg-surface-100 transition-colors"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="text-sm flex-1 text-left">Search</span>
            <span className="text-[9px] font-semibold uppercase tracking-wider bg-accent-100 text-accent-700 px-1.5 py-0.5 rounded">new</span>
            <kbd className="text-[10px] border border-surface-200 rounded px-1 py-0.5 bg-surface-0">⌘K</kbd>
          </button>
        </div>

        {trialChipText && (
          <div className="px-4 pt-3 flex-shrink-0">
            <div className={`text-xs font-medium text-center px-3 py-2 rounded-xl border ${trialTierColors(trialDays)}`}>
              {trialChipText}
            </div>
          </div>
        )}

        {/* Main navigation */}
        <div className="px-2 pt-4 flex-shrink-0">
          <Menu menuItemStyles={menuItemStyles}>
            <MenuItem
              icon={<img src="/homev2.png" alt="home" className={`h-[18px] w-[18px] transition-opacity ${isActive('') ? 'opacity-100' : 'opacity-60'}`} />}
              component={<Link to="/" />}
              className={`rounded-xl transition-all ${isActive('') ? 'bg-surface-100 !text-surface-900' : ''}`}
              onClick={() => setActiveMenu('')}
            >
              Home
            </MenuItem>
            <MenuItem
              icon={(
                <svg className={`w-[18px] h-[18px] transition-opacity ${isActive('connections') ? 'opacity-100' : 'opacity-60'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              )}
              component={<Link to="/connections" />}
              onClick={() => setActiveMenu('connections')}
              className={`rounded-xl transition-all ${isActive('connections') ? 'bg-surface-100 !text-surface-900' : ''}`}
            >
              <div className="flex justify-between items-center w-full">
                <span>Connections</span>
              </div>
            </MenuItem>
            <MenuItem
              icon={(
                <svg className={`w-[18px] h-[18px] transition-opacity ${isActive('events') ? 'opacity-100' : 'opacity-60'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 17.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                </svg>
              )}
              component={<Link to="/events" />}
              onClick={() => setActiveMenu('events')}
              className={`rounded-xl transition-all ${isActive('events') ? 'bg-surface-100 !text-surface-900' : ''}`}
            >
              <div className="flex justify-between items-center w-full">
                <span>Events</span>
                <span className="text-[9px] font-semibold uppercase tracking-wider bg-accent-100 text-accent-700 px-1.5 py-0.5 rounded">new</span>
              </div>
            </MenuItem>
            <MenuItem
              icon={<img src="/webhooksv2.png" alt="webhooks" className={`h-[18px] w-[18px] transition-opacity ${isActive('webhooks') ? 'opacity-100' : 'opacity-60'}`} />}
              component={<Link to="/webhooks" />}
              className={`rounded-xl transition-all ${isActive('webhooks') ? 'bg-surface-100 !text-surface-900' : ''}`}
              onClick={() => setActiveMenu('webhooks')}
            >
              Webhooks
            </MenuItem>
            <MenuItem
              icon={(
                <svg className={`w-[18px] h-[18px] transition-opacity ${isActive('api-log') ? 'opacity-100' : 'opacity-60'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              )}
              component={<Link to="/api-log" />}
              className={`rounded-xl transition-all ${isActive('api-log') ? 'bg-surface-100 !text-surface-900' : ''}`}
              onClick={() => setActiveMenu('api-log')}
            >
              API Log
            </MenuItem>
            <MenuItem
              icon={(
                <svg className={`w-[18px] h-[18px] transition-opacity ${isActive('data-explorer') ? 'opacity-100' : 'opacity-60'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              )}
              component={<Link to="/data-explorer" />}
              className={`rounded-xl transition-all ${isActive('data-explorer') ? 'bg-surface-100 !text-surface-900' : ''}`}
              onClick={() => setActiveMenu('data-explorer')}
            >
              Data Explorer
            </MenuItem>
          </Menu>
        </div>

        {/* Bottom navigation */}
        <div className="mt-auto px-2 pb-4 pt-4 flex-shrink-0">
          <div className="border-t border-surface-200 mx-2 mb-4" />
          <Menu menuItemStyles={menuItemStyles}>
            <MenuItem
              icon={(
                <svg className={`w-[18px] h-[18px] transition-opacity ${isActive('integrations') ? 'opacity-100' : 'opacity-60'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
              )}
              component={<Link to="/integrations" />}
              className={`rounded-xl transition-all ${isActive('integrations') ? 'bg-surface-100 !text-surface-900' : ''}`}
              onClick={() => setActiveMenu('integrations')}
            >
              Integrations
            </MenuItem>

            <MenuItem
              icon={<img src="/externalLinkv2.png" alt="documentation" className="h-[18px] w-[18px] opacity-60" />}
              component={<Link to="https://docs.trackstarhq.com" target="_blank" />}
              className="rounded-xl"
            >
              Documentation
            </MenuItem>

            <MenuItem
              icon={<img src="/settingsv2.png" alt="settings" className={`h-[18px] w-[18px] transition-opacity ${isActive('settings') ? 'opacity-100' : 'opacity-60'}`} />}
              component={<Link to="/settings" />}
              className={`rounded-xl transition-all ${isActive('settings') ? 'bg-surface-100 !text-surface-900' : ''}`}
              onClick={() => setActiveMenu('settings')}
            >
              Settings
            </MenuItem>
          </Menu>

          {/* User section */}
          <div className="mt-4 mx-2">
            {showSwitcher && (
              <div className="mb-3 px-2">
                <CustomOrgSwitcher />
              </div>
            )}
            <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-surface-100/50">
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: {
                      width: '32px',
                      height: '32px',
                    },
                  },
                }}
              />
              <SignOutButton className="text-sm font-medium text-surface-500 hover:text-surface-700 transition-colors" />
            </div>
          </div>
        </div>
      </div>
    </Sidebar>
  );
}

export default Navbar;
